import { useState } from 'react';

import type { ChatAvailability as Availability } from '#shared/types/chat';
import { chatVisitor } from '~/lib/chat-visitor';
import { ChatFallbackForm } from './ChatFallbackForm';

const FIELD =
  'w-full rounded-md border border-hairline-strong bg-surface px-3 py-2 text-base text-ink placeholder:text-ink-3 focus:border-accent';
const LABEL = 'mb-1 block text-sm font-medium text-ink-2';

/** "morgen om 08:30" uit een ISO-tijdstip. De server rekent de tijdzone al
 *  goed uit; hier alleen nog leesbaar maken. */
function whenOpen(iso: string): string {
  const at = new Date(iso);
  const now = new Date();
  const time = at.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
  const days = Math.round((at.setHours(0, 0, 0, 0) - new Date(now).setHours(0, 0, 0, 0)) / 864e5);
  if (days <= 0) return `vandaag om ${time}`;
  if (days === 1) return `morgen om ${time}`;
  return `${new Date(iso).toLocaleDateString('nl-NL', { weekday: 'long' })} om ${time}`;
}

/**
 * De reden waarom er niet gechat kan worden, in gewone taal.
 *
 * NOOIT alleen "offline". Dit is het hele verschil met het bronproject: daar
 * ontdekt de bezoeker pas na drie minuten wachten dat er niemand was.
 */
function reasonText(a: Availability | null): { title: string; detail: string } {
  if (!a) {
    return {
      title: 'We kunnen de bezetting nu niet ophalen',
      detail: 'Laat hieronder een bericht achter, dan pakken we het per e-mail op.',
    };
  }
  switch (a.reason) {
    case 'gesloten':
      return {
        title: 'We zijn nu gesloten',
        detail: a.opensAt
          ? `Onze chat is bereikbaar ${a.officeHoursLabel}. Weer open: ${whenOpen(a.opensAt)}.`
          : `Onze chat is bereikbaar ${a.officeHoursLabel}.`,
      };
    case 'niemand':
      return {
        title: 'Er is nu niemand beschikbaar',
        detail: 'Alle medewerkers zijn in gesprek of afwezig.',
      };
    case 'druk':
      return {
        title: 'Het is nu erg druk',
        detail: 'De wachtrij zit vol. Laat een bericht achter, dan reageren we snel.',
      };
    default:
      return {
        title: 'De chat is nu niet beschikbaar',
        detail: 'Laat hieronder een bericht achter, dan pakken we het per e-mail op.',
      };
  }
}

/* ─────────────────────────────  Bezig  ───────────────────────────────── */

export function ChatProbing() {
  return (
    <div className="flex flex-col gap-2 p-6" aria-busy>
      <p className="text-base text-ink-2">Even kijken wie er beschikbaar is…</p>
      <div className="skeleton h-4 w-2/3 rounded" />
      <div className="skeleton h-4 w-1/2 rounded" />
    </div>
  );
}

/* ─────────────────────────  Er is iemand  ────────────────────────────── */

export function ChatAvailableForm({
  availability, busy, error,
}: { availability: Availability; busy: boolean; error: string }) {
  const [name, setName] = useState('');
  const [topic, setTopic] = useState('');

  const count = availability.count;

  return (
    <form
      className="flex flex-col gap-3 p-4"
      onSubmit={(e) => {
        e.preventDefault();
        void chatVisitor.start(name, topic);
      }}
    >
      {/* De stip is nooit de enige drager van betekenis — vereist voor 1.4.1, en
          in forced-colors verdwijnt de vulling sowieso. */}
      <p className="flex items-center gap-2 text-base text-ink-body">
        <span className="size-2 shrink-0 rounded-full bg-success" aria-hidden />
        <span>
          <strong className="font-semibold">
            {count === 1 ? '1 medewerker' : `${count} medewerkers`} online
          </strong>{' '}
          — meestal binnen een minuut antwoord.
        </span>
      </p>

      <div>
        <label className={LABEL} htmlFor="kb-chat-start-name">Hoe heet je?</label>
        <input id="kb-chat-start-name" className={FIELD} value={name} required maxLength={60}
               autoComplete="given-name" onChange={(e) => setName(e.target.value)} />
      </div>

      <div>
        <label className={LABEL} htmlFor="kb-chat-start-topic">Waar gaat het over?</label>
        <textarea id="kb-chat-start-topic" className={`${FIELD} min-h-20 resize-y`} value={topic}
                  required maxLength={500} placeholder="Bijvoorbeeld: mijn pinautomaat geeft foutcode 42"
                  onChange={(e) => setTopic(e.target.value)} />
      </div>

      {error && (
        <p role="alert" className="rounded-md bg-danger-soft px-3 py-2 text-sm text-danger">{error}</p>
      )}

      <button
        type="submit"
        disabled={busy}
        className="rounded-md bg-accent px-3 py-2 text-base font-semibold text-accent-on shadow-sm transition hover:bg-accent-hover hover:shadow-glow disabled:opacity-50"
      >
        {busy ? 'Bezig…' : 'Chat starten'}
      </button>

      <p className="text-sm text-ink-3">Bereikbaar {availability.officeHoursLabel}.</p>
    </form>
  );
}

/* ───────────────────────  Er is niemand  ─────────────────────────────── */

export function ChatUnavailable({
  availability, busy, error,
}: { availability: Availability | null; busy: boolean; error: string }) {
  const { title, detail } = reasonText(availability);
  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-1 border-b border-hairline px-4 pt-4 pb-3">
        <p className="flex items-center gap-2 text-base font-semibold text-ink">
          <span className="size-2 shrink-0 rounded-full bg-warning" aria-hidden />
          {title}
        </p>
        <p className="text-sm text-ink-2">{detail}</p>
      </div>
      <ChatFallbackForm busy={busy} error={error} />
    </div>
  );
}
